// components/entities/PortfolioRenderer.tsx
import React, { useCallback } from 'react';
import { useKernelStore } from '@/kernel/kernelStores';
import { useKernel } from '@/kernel/Kernel';
import type { BaseEntityProps, EntityRowAction, LeasingPortfolioDTO } from '#types/types';
import { Building2, MapPin, Globe } from '@/components/ui/lucide-memo';
import clsx from 'clsx';
import {
  formatEnumLabel,
  formatWebsiteLabel,
  intentBadgeClass,
  normalizeWebsiteUrls,
  parseWebsiteUrlsText,
} from '#lib/leasing/portfolio-display-utils';
import type { TableColumnDef } from '../portal/Table';
import styles from '../portal/Table.module.css';
import { EntitySkeleton } from './EntityView';
import { EntityRowActions } from './EntityRowActions';

const translations = {
  en: {
    noWebsite: 'No website',
    editWebsites: 'Edit Websites',
    websitesPrompt: 'Website URLs (comma or newline separated):',
    editDeclaredAssets: 'Edit Declared Assets',
    declaredAssetsPrompt: 'Declared assets count:',
    invalidDeclaredAssets: 'Please enter a whole number between 0 and 2147483647.',
    dateLocale: 'en-GB',
  },
  fr: {
    noWebsite: 'Pas de site',
    editWebsites: 'Modifier sites',
    websitesPrompt: 'URLs des sites (separes par virgule ou saut de ligne):',
    editDeclaredAssets: 'Modifier actifs declares',
    declaredAssetsPrompt: 'Nombre d actifs declares:',
    invalidDeclaredAssets: 'Saisissez un entier entre 0 et 2147483647.',
    dateLocale: 'fr-FR',
  },
};

const formatDate = (value?: string | null, locale?: string) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return Intl.DateTimeFormat(locale || 'en-GB', { dateStyle: 'medium' }).format(date);
};

export interface PortfolioRendererProps extends BaseEntityProps<LeasingPortfolioDTO> {
  isExpanded?: boolean;
  columns?: TableColumnDef[];
  rowActions?: EntityRowAction<LeasingPortfolioDTO>[];
}

const CELL_JUSTIFY_END = { justifyContent: 'flex-end' } as const;
const CELL_JUSTIFY_CENTER = { justifyContent: 'center' } as const;
const CELL_JUSTIFY_START = { justifyContent: 'flex-start' } as const;

const cellJustifyStyle = (column: TableColumnDef) => {
  if (column.id === 'actions' || column.align === 'right') return CELL_JUSTIFY_END;
  if (column.align === 'center') return CELL_JUSTIFY_CENTER;
  return CELL_JUSTIFY_START;
};

const DEFAULT_PORTFOLIO_COLUMNS: TableColumnDef[] = [
  { id: 'portfolio', header: '', minWidth: 200 },
  { id: 'type', header: '', minWidth: 100 },
  { id: 'category', header: '', minWidth: 100 },
  { id: 'location', header: '', minWidth: 140 },
  { id: 'intent', header: '', minWidth: 80 },
  { id: 'properties', header: '', minWidth: 80, align: 'right' },
  { id: 'status', header: '', minWidth: 100 },
  { id: 'updated', header: '', minWidth: 100, align: 'right' },
  { id: 'actions', header: '', minWidth: 100, align: 'right' },
];

export const PortfolioRenderer = React.memo(({
  entityId,
  variant = 'row',
  isExpanded,
  onClick,
  className,
  columns,
  rowActions = [],
}: PortfolioRendererProps) => {
  const { kernel } = useKernel();
  const lang = useKernelStore(state => state.language);
  const t = translations[lang];

  const data = useKernelStore(state => state.inventory.Portfolio.registry[entityId]) as LeasingPortfolioDTO | undefined;

  const isFaterOrgMember = useKernelStore(state => state.userContext.isFaterOrgMember);
  const isOrganisationAdmin = useKernelStore(state => state.userContext.isOrganisationAdmin);

  const handleRowClick = useCallback(() => {
    if (!data) return;
    if (onClick) onClick();
  }, [onClick, data]);

  const handleContextMenu = useCallback((e: React.MouseEvent) => {
    if (!kernel.current || !data) return;
    const canEditWebsite = isFaterOrgMember;
    const canEditDeclaredAssets = isFaterOrgMember || isOrganisationAdmin;
    if (!canEditWebsite && !canEditDeclaredAssets) return;

    e.preventDefault();

    const actions: Array<{ label: string; icon: React.ReactElement; onClick: () => void }> = [];

    if (canEditWebsite) {
      actions.push({
        label: t.editWebsites,
        icon: <Globe size={14} />,
        onClick: () => {
          const currentValue = normalizeWebsiteUrls(data.websiteUrls).join('\n');
          const nextValue = window.prompt(t.websitesPrompt, currentValue);
          if (nextValue !== null) {
            kernel.current?.updateEntity('Portfolio', data.id, {
              websiteUrls: parseWebsiteUrlsText(nextValue),
            });
          }
        }
      });
    }

    if (canEditDeclaredAssets) {
      actions.push({
        label: t.editDeclaredAssets,
        icon: <Building2 size={14} />,
        onClick: () => {
          const val = window.prompt(t.declaredAssetsPrompt, String(data.propertyCount ?? 0));
          if (val === null) return;
          const trimmed = val.trim();
          if (trimmed.length === 0) {
            window.alert(t.invalidDeclaredAssets);
            return;
          }

          const parsed = Number(trimmed);
          if (!Number.isInteger(parsed) || parsed < 0 || parsed > 2147483647) {
            window.alert(t.invalidDeclaredAssets);
            return;
          }

          kernel.current?.updateEntity('Portfolio', data.id, { propertyCount: parsed });
        }
      });
    }

    kernel.current.setMenuData({
      position: { x: e.clientX, y: e.clientY },
      actions
    });
  }, [isFaterOrgMember, isOrganisationAdmin, kernel, data, t]);

  if (!data) return <EntitySkeleton variant={variant} className={className} />;

  const initial = data.name?.[0]?.toUpperCase() ?? '?';
  const websiteLabel = formatWebsiteLabel(normalizeWebsiteUrls(data.websiteUrls)[0] ?? null);
  const locationLabel = [data.city, data.country].filter(Boolean).join(', ') || '-';

  if (variant === 'chip') {
    return (
      <div className={clsx('flex items-center gap-2 px-2 py-1 rounded bg-white/5 border border-white/10 text-xs text-white/80', className)}>
        <Building2 size={12} />
        <span className="truncate">{data.name}</span>
      </div>
    );
  }

  if (variant === 'card') {
    return (
      <div
        className={clsx(
          'group flex aspect-square w-full cursor-pointer flex-col overflow-hidden rounded-xl border border-white/10 bg-white/5 transition-all hover:border-white/20 hover:bg-white/10',
          className
        )}
        onClick={handleRowClick}
        onContextMenu={handleContextMenu}
      >
        <div className="relative flex min-h-0 flex-1 items-center justify-center bg-white/[0.02] p-4">
          {data.logoUrl ? (
            <img
              src={data.logoUrl}
              alt={data.name}
              className="max-h-full max-w-full object-contain"
              loading="lazy"
            />
          ) : (
            <div className="flex h-16 w-16 items-center justify-center rounded-xl border border-white/10 bg-white/5 text-xl font-semibold text-white/70">
              {initial}
            </div>
          )}
          <div className="absolute right-2 top-2 rounded-full border border-white/12 bg-black/25 px-2 py-0.5 text-[10px] text-white/75 backdrop-blur-sm">
            {(data.realPropertyCount ?? 0).toLocaleString()}
          </div>
        </div>
        <div className="border-t border-white/10 px-2.5 py-2">
          <div className="truncate text-[13px] font-medium text-white/90" title={data.name}>
            {data.name}
          </div>
          <div className="mt-0.5 truncate text-[11px] text-white/45">
            {locationLabel}
          </div>
        </div>
      </div>
    );
  }

  if (variant === 'row') {
    const cellRenderers: Record<string, React.ReactNode> = {
      portfolio: (
        <div className="flex items-center gap-3 min-w-0 h-full">
          <div className="w-8 h-8 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-xs text-white/70 shrink-0 overflow-hidden">
            {data.logoUrl ? (
              <img src={data.logoUrl} alt={data.name} className="w-full h-full object-cover" />
            ) : (
              <span>{initial}</span>
            )}
          </div>
          <div className="flex flex-col min-w-0">
            <span className="font-medium text-white text-sm truncate" title={data.name}>{data.name}</span>
            <span className="text-[10px] text-white/40 truncate">{websiteLabel || t.noWebsite}</span>
          </div>
        </div>
      ),
      type: (
        <div className="text-white/70 text-xs truncate h-full flex items-center">
          {formatEnumLabel(data.type)}
        </div>
      ),
      category: (
        <div className="text-white/70 text-xs truncate h-full flex items-center">
          {formatEnumLabel(data.category)}
        </div>
      ),
      location: (
        <div className="flex items-center gap-2 text-white/60 text-xs truncate h-full">
          <MapPin size={12} className="shrink-0 opacity-60" />
          <span className="truncate">{locationLabel}</span>
        </div>
      ),
      intent: (
        <div className="h-full flex items-center">
          <span className={clsx('text-[10px] px-2 py-0.5 rounded-full border font-bold', intentBadgeClass(data.intentScore))}>
            {data.intentScore ?? 'N/A'}
          </span>
        </div>
      ),
      properties: (
        <div className="text-right h-full flex items-center justify-end w-full">
          <div className="flex flex-col items-end leading-tight">
            <span className="text-white/85 text-xs">{data.realPropertyCount ?? 0}</span>
            {typeof data.realPropertyCount === 'number' &&
            typeof data.propertyCount === 'number' &&
            data.propertyCount !== data.realPropertyCount ? (
              <span className="text-[10px] text-white/35">decl: {data.propertyCount}</span>
            ) : null}
          </div>
        </div>
      ),
      status: (
        <div className="text-white/70 text-xs truncate h-full flex items-center">
          {formatEnumLabel(data.status)}
        </div>
      ),
      updated: (
        <div className="text-right text-white/40 text-xs h-full flex items-center justify-end w-full">
          {formatDate(data.updatedAt, t.dateLocale)}
        </div>
      ),
      actions: (
        <div className="text-right h-full flex items-center justify-end w-full">
          <EntityRowActions entityType="Portfolio" entity={data} rowActions={rowActions} />
        </div>
      ),
    };

    const activeColumns = columns || DEFAULT_PORTFOLIO_COLUMNS;

    return (
      <div
        className={clsx(
          styles.gridRow,
          styles.bodyRow,
          'group w-full cursor-pointer border-b border-white/5 !bg-transparent hover:!bg-white/[0.05]',
          isExpanded && 'bg-white/[0.02]',
          className
        )}
        onClick={handleRowClick}
        onContextMenu={handleContextMenu}
      >
        {activeColumns.map((column) => (
          <div
            key={column.id}
            className={clsx(styles.cell, column.id === 'actions' && styles.actionCell)}
            style={cellJustifyStyle(column)}
          >
            {cellRenderers[column.id] || null}
          </div>
        ))}
      </div>
    );
  }

  return null;
});

PortfolioRenderer.displayName = 'PortfolioRenderer';
